/**
 * Embarcação da frota disponível para consulta (RN001). A HU 812 é exclusivamente
 * de leitura (RN003): não há cadastro, edição nem exclusão no módulo.
 */
export interface Embarcacao {
  id: string;
  /** Sigla de identificação da embarcação — coluna Acrônimo. */
  acronimo: string;
  /** Nome da embarcação — coluna Embarcação. */
  nome: string;
  /** Tipo da embarcação — coluna Tipo. */
  tipo: string;
  /** Situação da embarcação — coluna Situação. */
  situacao: string;
}

/** Opção de um filtro de coluna: o rótulo na tela e o valor enviado na consulta. */
export interface EmbarcacaoFilterOption {
  value: string;
  label: string;
}

/**
 * Colunas filtráveis da listagem. Cada uma carrega suas opções do serviço
 * `GET /embarcacoes/filtros/{coluna}` e vira um parâmetro de lista na consulta.
 */
export const EMBARCACAO_FILTER_COLUMNS = ["acronimo", "embarcacao", "tipo", "situacao"] as const;

export type EmbarcacaoFilterColumn = (typeof EMBARCACAO_FILTER_COLUMNS)[number];

export const EMBARCACAO_FILTER_LABELS: Record<EmbarcacaoFilterColumn, string> = {
  acronimo: "Acrônimo",
  embarcacao: "Embarcação",
  tipo: "Tipo",
  situacao: "Situação",
};

/**
 * Critérios da área de filtros (AC3): cada coluna aceita vários valores e as
 * colunas são combináveis entre si e com a busca.
 */
export type EmbarcacoesFilters = Record<EmbarcacaoFilterColumn, string[]>;

export const EMPTY_EMBARCACOES_FILTERS: EmbarcacoesFilters = {
  acronimo: [],
  embarcacao: [],
  tipo: [],
  situacao: [],
};

/** Há ao menos um valor escolhido em alguma coluna — decide o empty state de filtros. */
export function hasEmbarcacoesFilters(filters: EmbarcacoesFilters): boolean {
  return EMBARCACAO_FILTER_COLUMNS.some((column) => filters[column].length > 0);
}

/**
 * Colunas de dado ordenáveis. A coluna de comandos da linha (a lupa) não carrega
 * dado comparável e por isso fica de fora. `nome` é o campo do contrato por trás
 * da coluna Embarcação.
 */
export type EmbarcacoesSortField = "acronimo" | "nome" | "tipo" | "situacao";

export const EMBARCACOES_SORT_FIELDS: EmbarcacoesSortField[] = [
  "acronimo",
  "nome",
  "tipo",
  "situacao",
];

export type EmbarcacoesSortOrder = "asc" | "desc";

export interface EmbarcacoesSort {
  field: EmbarcacoesSortField;
  direction: EmbarcacoesSortOrder;
}

/** Ordem padrão da tela: pelo acrônimo da embarcação, de A a Z. */
export const DEFAULT_EMBARCACOES_SORT: EmbarcacoesSort = { field: "acronimo", direction: "asc" };

/** Leitura tolerante do campo devolvido pelo cabeçalho da tabela. */
export function isEmbarcacoesSortField(value: unknown): value is EmbarcacoesSortField {
  return (
    typeof value === "string" && EMBARCACOES_SORT_FIELDS.includes(value as EmbarcacoesSortField)
  );
}

/** Formato do parâmetro `sort` do contrato da HU 812 (ex.: `nome:asc`). */
export function formatEmbarcacoesSort({ field, direction }: EmbarcacoesSort): string {
  return `${field}:${direction}`;
}

export interface ListEmbarcacoesParams {
  /** Texto livre do campo Buscar Embarcação; o serviço aplica correspondência parcial. */
  busca?: string;
  filters?: Partial<EmbarcacoesFilters>;
  /** Página 1-based, como no contrato. */
  page?: number;
  pageSize?: number;
  sort?: EmbarcacoesSort;
  signal?: AbortSignal;
}

export interface EmbarcacoesPage {
  embarcacoes: Embarcacao[];
  total: number;
}

export type EmbarcacoesStatus = "loading" | "success" | "error" | "forbidden";

export interface EmbarcacoesQueryState {
  status: EmbarcacoesStatus;
  embarcacoes: Embarcacao[];
  total: number;
  errorMessage: string | null;
}

export interface ListEmbarcacaoFilterOptionsParams {
  column: EmbarcacaoFilterColumn;
  signal?: AbortSignal;
}

/**
 * Seção Base Técnica do detalhamento da embarcação (HU 1724 — `EmbarcacaoBaseTecnicaDTO`).
 *
 * Todo campo é `string | null`: `null` é o "campo sem preenchimento" da tela
 * (AC4) e precisa ser distinguível de um valor vazio devolvido pela fonte. Os
 * campos numéricos do contrato (anos, idade, POB) chegam como número e são
 * guardados como o seu texto — a tela não calcula nem deriva nada (RN005).
 */
export interface EmbarcacaoBaseTecnica {
  nome: string | null;
  nomeAntigo: string | null;
  nomeAbreviado: string | null;
  acronimo: string | null;
  tipo: string | null;
  empresaOrigem: string | null;
  mmsi: string | null;
  vesselSignalLetters: string | null;
  imo: string | null;
  grupo: string | null;
  /** Sem máscara e possivelmente alfanumérico: a máscara é da tela (AC4). */
  cnpjFaturamento: string | null;
  armador: string | null;
  /** Sem máscara e possivelmente alfanumérico: a máscara é da tela (AC4). */
  cnpj: string | null;
  /** Pode trazer vários centros de custo em um único texto (ex.: `X` de Obra e `Y` de Manutenção). */
  centroCusto: string | null;
  /** Pode trazer várias embarcações irmãs em um único texto. */
  sisterVessels: string | null;
  possuiReb: string | null;
  bandeiraSuspensa: string | null;
  bandeira: string | null;
  paisConstrucao: string | null;
  estaleiroConstrucao: string | null;
  anoBatimentoQuilha: string | null;
  anoFinalConstrucao: string | null;
  idade: string | null;
  anoModernizacao: string | null;
  sistemaGerencial: string | null;
  totalPessoas: string | null;
  situacaoEmbarcacao: string | null;
  /** Data da última atualização das informações da seção. */
  ultimaAtualizacao: string | null;
}

/** Os 27 campos da seção; a data da última atualização acompanha o título (AC3). */
export const EMBARCACAO_BASE_TECNICA_FIELDS = [
  "nome",
  "nomeAntigo",
  "nomeAbreviado",
  "acronimo",
  "tipo",
  "empresaOrigem",
  "mmsi",
  "vesselSignalLetters",
  "imo",
  "grupo",
  "cnpjFaturamento",
  "armador",
  "cnpj",
  "centroCusto",
  "sisterVessels",
  "possuiReb",
  "bandeiraSuspensa",
  "bandeira",
  "paisConstrucao",
  "estaleiroConstrucao",
  "anoBatimentoQuilha",
  "anoFinalConstrucao",
  "idade",
  "anoModernizacao",
  "sistemaGerencial",
  "totalPessoas",
  "situacaoEmbarcacao",
] as const satisfies readonly (keyof EmbarcacaoBaseTecnica)[];

export type EmbarcacaoBaseTecnicaField = (typeof EMBARCACAO_BASE_TECNICA_FIELDS)[number];

/**
 * A seção não tem nenhuma informação a apresentar — nem os campos, nem a data
 * de atualização. É o estado vazio do padrão global do Hub (AC5), distinto da
 * embarcação inexistente (404).
 */
export function isEmbarcacaoBaseTecnicaEmpty(baseTecnica: EmbarcacaoBaseTecnica): boolean {
  return Object.values(baseTecnica).every((value) => value === null);
}

/** Consulta somente leitura de uma seção da embarcação acionada na lupa (RN001/RN004). */
export interface GetEmbarcacaoSectionParams {
  /** Identificador técnico da embarcação, vindo da linha selecionada na listagem. */
  id: string;
  signal?: AbortSignal;
}

export type GetEmbarcacaoBaseTecnicaParams = GetEmbarcacaoSectionParams;

/**
 * Seção Dimensões Principais do detalhamento da embarcação (HU 1728 —
 * `EmbarcacaoDimensoesDTO`).
 *
 * Reúne, em um único contrato, os três agrupamentos da planilha de origem —
 * Dimensões Principais, Cargo Deck e Plano de docagem —, que na tela formam uma
 * lista contínua de campos, sem subagrupamento visual (AC2).
 *
 * Os campos dimensionais trafegam como número, sem unidade: a unidade é do
 * rótulo na tela. `null` é o "campo sem preenchimento" (AC4) e precisa ser
 * distinguível de zero, que é um valor legítimo da fonte.
 */
export interface EmbarcacaoDimensoes {
  comprimentoTotal: number | null;
  comprimentoEntrePerpendiculares: number | null;
  caladoMax: number | null;
  caladoDocagem: number | null;
  bocaMoldada: number | null;
  arqueacaoBruta: number | null;
  arqueacaoLiquida: number | null;
  tonelagemPorteBruto: number | null;
  /** Data de atualização do agrupamento Dimensões Principais (RN006). */
  atualizacaoDimensoes: string | null;
  comprimentoConves: number | null;
  larguraConves: number | null;
  areaTotalConves: number | null;
  resistenciaConves: number | null;
  capacidadeConves: number | null;
  comprimentoConvesLivreUtil: number | null;
  larguraConvesLivreUtil: number | null;
  areaConvesLivreUtil: number | null;
  /** Data de atualização do agrupamento Cargo Deck (RN006). */
  atualizacaoCargoDeck: string | null;
  deslocamentoCaladoDocagem: number | null;
  caladoHidrostaticoDocagem: number | null;
  tamanhoApendices: number | null;
  /** Texto da fonte, hoje identificado como SIM / NÃO na planilha técnica. */
  equipamentoRetratil: string | null;
  caladoRealDocagem: number | null;
  /** Data de atualização do agrupamento Plano de docagem (RN006). */
  atualizacaoDocagem: string | null;
  /** Data da última atualização da seção, exibida no cabeçalho (AC3). */
  ultimaAtualizacao: string | null;
}

/**
 * Os 24 campos da seção, na ordem do AC2: os 21 campos dimensionais e as três
 * datas de conjunto, que entram como campos comuns da lista. A data da última
 * atualização da seção fica de fora — ela acompanha o título (AC3).
 */
export const EMBARCACAO_DIMENSOES_FIELDS = [
  "comprimentoTotal",
  "comprimentoEntrePerpendiculares",
  "caladoMax",
  "caladoDocagem",
  "bocaMoldada",
  "arqueacaoBruta",
  "arqueacaoLiquida",
  "tonelagemPorteBruto",
  "atualizacaoDimensoes",
  "comprimentoConves",
  "larguraConves",
  "areaTotalConves",
  "resistenciaConves",
  "capacidadeConves",
  "comprimentoConvesLivreUtil",
  "larguraConvesLivreUtil",
  "areaConvesLivreUtil",
  "atualizacaoCargoDeck",
  "deslocamentoCaladoDocagem",
  "caladoHidrostaticoDocagem",
  "tamanhoApendices",
  "equipamentoRetratil",
  "caladoRealDocagem",
  "atualizacaoDocagem",
] as const satisfies readonly (keyof EmbarcacaoDimensoes)[];

export type EmbarcacaoDimensoesField = (typeof EMBARCACAO_DIMENSOES_FIELDS)[number];

/**
 * A seção não tem nenhuma informação a apresentar — nem os campos, nem as datas
 * de atualização. É o estado vazio do padrão global do Hub (AC4), distinto da
 * embarcação inexistente (404).
 */
export function isEmbarcacaoDimensoesEmpty(dimensoes: EmbarcacaoDimensoes): boolean {
  return Object.values(dimensoes).every((value) => value === null);
}

/** Consulta somente leitura das dimensões da embarcação consultada (RN004). */
export interface GetEmbarcacaoDimensoesParams {
  /** Identificador técnico da embarcação, vindo da linha selecionada na listagem. */
  id: string;
  signal?: AbortSignal;
}

/**
 * Seção Consumo Modo Operação do detalhamento da embarcação (HU 1730).
 *
 * Os seis consumos chegam do contrato como decimal, em m³/dia (RN002), e são
 * guardados como o seu texto — a tela não recalcula nada. As duas datas chegam
 * sem hora. Como na Base Técnica, `null` é o "campo sem preenchimento" (AC3).
 */
export interface EmbarcacaoConsumoOperacao {
  consumoDp: string | null;
  consumoFdAt: string | null;
  consumoNvVelServico: string | null;
  consumoNvVelTotal: string | null;
  consumoSm: string | null;
  consumoNvVelEconomica: string | null;
  /** Data de atualização dos valores de consumo. */
  atualizacaoConsumo: string | null;
  /** Data da última atualização das informações da seção. */
  ultimaAtualizacao: string | null;
}

export type EmbarcacaoConsumoOperacaoField = keyof EmbarcacaoConsumoOperacao;

/** Nenhum dos 8 campos tem valor: estado vazio do padrão global do Hub (AC3). */
export function isEmbarcacaoConsumoOperacaoEmpty(consumo: EmbarcacaoConsumoOperacao): boolean {
  return Object.values(consumo).every((value) => value === null);
}

export type GetEmbarcacaoConsumoOperacaoParams = GetEmbarcacaoBaseTecnicaParams;

/**
 * Estados de uma seção do detalhamento (AC5). Cada cenário tem tratamento
 * próprio: `sessionExpired` (401), `forbidden` (403), `notFound` (404), `empty`
 * para a seção sem informações e `error` para a falha genérica. As seções que
 * vierem depois da Base Técnica reaproveitam os mesmos estados.
 */
export type EmbarcacaoDetailStatus =
  "loading" | "success" | "sessionExpired" | "forbidden" | "notFound" | "empty" | "error";

export interface EmbarcacaoBaseTecnicaState {
  status: EmbarcacaoDetailStatus;
  /** Preenchido apenas em `success`: nenhum outro estado exibe campo da seção. */
  baseTecnica: EmbarcacaoBaseTecnica | null;
  errorMessage: string | null;
}

/**
 * Seção Classe do detalhamento da embarcação (HU 1726 — `EmbarcacaoClasseDTO`).
 *
 * Como na Base Técnica, todo campo é `string | null`: `null` é o "campo sem
 * preenchimento" da tela (AC3). As datas chegam como texto do contrato e são
 * guardadas exatamente assim — a formatação para leitura é da seção, e nenhum
 * valor é calculado ou derivado (AC3).
 */
export interface EmbarcacaoClasse {
  notacaoClasse: string | null;
  validadeNotacaoClasse: string | null;
  classificadoraAtual: string | null;
  ultimoReportClasse: string | null;
  anualFrom: string | null;
  anualTo: string | null;
  especialFrom: string | null;
  especialTo: string | null;
  ultimaEspecial: string | null;
  intermediariaFrom: string | null;
  intermediariaTo: string | null;
  ultimaIntermediaria: string | null;
  ultimaBottom: string | null;
  condicaoUltimaBottom: string | null;
  bottomTo: string | null;
  thrusterTo: string | null;
  propulsaoTo: string | null;
  ultimoFmea: string | null;
  vencimentoFmea: string | null;
  /**
   * Data da última atualização das informações da seção, exibida junto ao
   * título (AC4). O contrato traz ainda `atualizacaoClasse`, que não consta dos
   * 19 campos da história: é `ultimaAtualizacao` que alimenta o cabeçalho, como
   * em todas as seções do detalhamento.
   */
  ultimaAtualizacao: string | null;
}

/** Os 19 campos da seção; a data da última atualização acompanha o título (AC2). */
export const EMBARCACAO_CLASSE_FIELDS = [
  "notacaoClasse",
  "validadeNotacaoClasse",
  "classificadoraAtual",
  "ultimoReportClasse",
  "anualFrom",
  "anualTo",
  "especialFrom",
  "especialTo",
  "ultimaEspecial",
  "intermediariaFrom",
  "intermediariaTo",
  "ultimaIntermediaria",
  "ultimaBottom",
  "condicaoUltimaBottom",
  "bottomTo",
  "thrusterTo",
  "propulsaoTo",
  "ultimoFmea",
  "vencimentoFmea",
] as const satisfies readonly (keyof EmbarcacaoClasse)[];

export type EmbarcacaoClasseField = (typeof EMBARCACAO_CLASSE_FIELDS)[number];

/**
 * A embarcação não tem nenhuma informação de classe a apresentar — nem os
 * campos, nem a data de atualização. É o estado vazio do padrão global do Hub
 * (AC5), distinto da embarcação inexistente (404).
 */
export function isEmbarcacaoClasseEmpty(classe: EmbarcacaoClasse): boolean {
  return Object.values(classe).every((value) => value === null);
}

export type GetEmbarcacaoClasseParams = GetEmbarcacaoSectionParams;

export interface EmbarcacaoClasseState {
  status: EmbarcacaoDetailStatus;
  /** Preenchido apenas em `success`: nenhum outro estado exibe campo da seção. */
  classe: EmbarcacaoClasse | null;
  errorMessage: string | null;
}

/**
 * Seção Comercial do detalhamento da embarcação (HU 1725 — `EmbarcacaoComercialDTO`).
 *
 * Como na Base Técnica, todo campo é `string | null` e `null` é o "campo sem
 * preenchimento" da tela (AC4). As datas do contrato ficam guardadas como o
 * texto recebido: a formatação é da apresentação, e nada aqui é calculado nem
 * derivado (AC3).
 */
export interface EmbarcacaoComercial {
  clienteAtual: string | null;
  observacoes: string | null;
  inicioContrato: string | null;
  fimContrato: string | null;
  /** Declarado como data no contrato, mas a fonte pode trazer outro texto. */
  extensaoContrato: string | null;
  /** Data da última atualização das informações da seção. */
  ultimaAtualizacao: string | null;
}

/**
 * Os 5 campos da seção, na ordem do protótipo — que pareia Cliente Atual com
 * Observações e só então os campos de contrato. A data da última atualização
 * não é um campo: acompanha o título (AC1).
 */
export const EMBARCACAO_COMERCIAL_FIELDS = [
  "clienteAtual",
  "observacoes",
  "inicioContrato",
  "fimContrato",
  "extensaoContrato",
] as const satisfies readonly (keyof EmbarcacaoComercial)[];

export type EmbarcacaoComercialField = (typeof EMBARCACAO_COMERCIAL_FIELDS)[number];

/**
 * A seção não tem nenhuma informação a apresentar — nem os campos, nem a data
 * de atualização. É o estado vazio do padrão global do Hub (AC4), distinto da
 * embarcação inexistente (404).
 */
export function isEmbarcacaoComercialEmpty(comercial: EmbarcacaoComercial): boolean {
  return Object.values(comercial).every((value) => value === null);
}

/** Consulta somente leitura da seção da embarcação acionada na lupa (AC5). */
export interface GetEmbarcacaoComercialParams {
  /** Identificador técnico da embarcação, vindo da linha selecionada na listagem. */
  id: string;
  signal?: AbortSignal;
}

export interface EmbarcacaoComercialState {
  status: EmbarcacaoDetailStatus;
  /** Preenchido apenas em `success`: nenhum outro estado exibe campo da seção. */
  comercial: EmbarcacaoComercial | null;
  errorMessage: string | null;
}

export interface EmbarcacaoDimensoesState {
  status: EmbarcacaoDetailStatus;
  /** Preenchido apenas em `success`: nenhum outro estado exibe campo da seção (AC5). */
  dimensoes: EmbarcacaoDimensoes | null;
  errorMessage: string | null;
}

/**
 * Seção Tanques do detalhamento da embarcação (HU 1732 — `EmbarcacaoTanquesDTO`).
 *
 * As capacidades trafegam como número, em m³ e sem unidade: a unidade é do
 * rótulo na tela. `null` é o "campo sem preenchimento" (AC3) e precisa ser
 * distinguível de zero, que é um valor legítimo da fonte (AC2).
 */
export interface EmbarcacaoTanques {
  capacidadeTotalCombustivel: number | null;
  capacidadeTotalAgua: number | null;
  capacidadeTotalOro: number | null;
  capacidadeTotalAguaLastro: number | null;
  capacidadeTotalOleoBase: number | null;
  capacidadeTotalSalmoura: number | null;
  capacidadeTotalLama: number | null;
  capacidadeTotalGranel: number | null;
  /** Texto livre da fonte, com até 255 caracteres, exibido sem corte (AC3). */
  comentarios: string | null;
  /** Data de atualização das capacidades, exibida como campo da lista. */
  atualizacaoCapacidade: string | null;
  /** Data da última atualização da seção, exibida no cabeçalho (AC2). */
  ultimaAtualizacao: string | null;
}

/**
 * Seção Vazão das Bombas do detalhamento da embarcação (HU 1735 —
 * `EmbarcacaoVazaoBombasDTO`).
 *
 * Vazão (m³/h) e pressão (bar) de cada produto trafegam como número, sem
 * unidade: a unidade é do rótulo na tela (RN001). `null` é o "campo sem
 * preenchimento" (AC3) e precisa ser distinguível de zero, que é um valor
 * legítimo da fonte.
 */
export interface EmbarcacaoVazaoBombas {
  vazaoCombustivel: number | null;
  pressaoCombustivel: number | null;
  vazaoAgua: number | null;
  pressaoAgua: number | null;
  vazaoOro: number | null;
  pressaoOro: number | null;
  vazaoAguaLastro: number | null;
  pressaoAguaLastro: number | null;
  vazaoOleoBase: number | null;
  pressaoOleoBase: number | null;
  vazaoSalmoura: number | null;
  pressaoSalmoura: number | null;
  vazaoLama: number | null;
  pressaoLama: number | null;
  vazaoGranel: number | null;
  pressaoGranel: number | null;
  /** Data de atualização das informações de vazão e pressão (RN002). */
  atualizacaoVazaoBomba: string | null;
  /** Data da última atualização das informações da seção. */
  ultimaAtualizacao: string | null;
}

/**
 * Os 10 campos da seção, na ordem do AC2: as 8 capacidades, os comentários e a
 * data de atualização da capacidade. A data da última atualização fica de fora
 * — ela acompanha o título.
 */
export const EMBARCACAO_TANQUES_FIELDS = [
  "capacidadeTotalCombustivel",
  "capacidadeTotalAgua",
  "capacidadeTotalOro",
  "capacidadeTotalAguaLastro",
  "capacidadeTotalOleoBase",
  "capacidadeTotalSalmoura",
  "capacidadeTotalLama",
  "capacidadeTotalGranel",
  "comentarios",
  "atualizacaoCapacidade",
] as const satisfies readonly (keyof EmbarcacaoTanques)[];

export type EmbarcacaoTanquesField = (typeof EMBARCACAO_TANQUES_FIELDS)[number];

/**
 * A seção não tem nenhuma informação a apresentar — nem as capacidades, nem as
 * datas. É o estado vazio do padrão global do Hub (AC4), distinto da
 * embarcação inexistente (404).
 */
export function isEmbarcacaoTanquesEmpty(tanques: EmbarcacaoTanques): boolean {
  return Object.values(tanques).every((value) => value === null);
}

export type GetEmbarcacaoTanquesParams = GetEmbarcacaoSectionParams;

export interface EmbarcacaoTanquesState {
  status: EmbarcacaoDetailStatus;
  /** Preenchido apenas em `success`: nenhum outro estado exibe campo da seção (AC4). */
  tanques: EmbarcacaoTanques | null;
  errorMessage: string | null;
}

export interface EmbarcacaoConsumoOperacaoState {
  status: EmbarcacaoDetailStatus;
  /** Preenchido apenas em `success`: nenhum outro estado exibe campo da seção. */
  consumo: EmbarcacaoConsumoOperacao | null;
  errorMessage: string | null;
}

/**
 * Os 17 campos da seção, na ordem do AC2: vazão e pressão de cada produto e,
 * por fim, a data de atualização da vazão. A data da última atualização fica
 * junto ao título, como nas demais seções.
 */
export const EMBARCACAO_VAZAO_BOMBAS_FIELDS = [
  "vazaoCombustivel",
  "pressaoCombustivel",
  "vazaoAgua",
  "pressaoAgua",
  "vazaoOro",
  "pressaoOro",
  "vazaoAguaLastro",
  "pressaoAguaLastro",
  "vazaoOleoBase",
  "pressaoOleoBase",
  "vazaoSalmoura",
  "pressaoSalmoura",
  "vazaoLama",
  "pressaoLama",
  "vazaoGranel",
  "pressaoGranel",
  "atualizacaoVazaoBomba",
] as const satisfies readonly (keyof EmbarcacaoVazaoBombas)[];

export type EmbarcacaoVazaoBombasField = (typeof EMBARCACAO_VAZAO_BOMBAS_FIELDS)[number];

/**
 * A seção não tem nenhuma informação a apresentar — nem os campos, nem as
 * datas. É o estado vazio do padrão global do Hub (AC3), distinto da
 * embarcação inexistente (404).
 */
export function isEmbarcacaoVazaoBombasEmpty(vazaoBombas: EmbarcacaoVazaoBombas): boolean {
  return Object.values(vazaoBombas).every((value) => value === null);
}

export type GetEmbarcacaoVazaoBombasParams = GetEmbarcacaoSectionParams;

export interface EmbarcacaoVazaoBombasState {
  status: EmbarcacaoDetailStatus;
  /** Preenchido apenas em `success`: nenhum outro estado exibe campo da seção (AC4). */
  vazaoBombas: EmbarcacaoVazaoBombas | null;
  errorMessage: string | null;
}

/**
 * Seção Sistemas de DP do detalhamento da embarcação (US-1799).
 *
 * Como nas demais seções de texto, todo campo é `string | null` e `null` é o
 * "campo sem preenchimento" da tela (AC3). A data `atualizacaoDp` chega como
 * texto do contrato e é guardada exatamente assim — a formatação para leitura é
 * da seção, e nenhum valor é calculado ou derivado (AC2/AC4).
 */
export interface EmbarcacaoSistemasDp {
  dp: string | null;
  fabricanteModelo: string | null;
  sistemaReferenciaRelativo1: string | null;
  sistemaReferenciaRelativo2: string | null;
  atualizacaoDp: string | null;
  /** Data da última atualização das informações da seção, exibida junto ao título (AC2). */
  ultimaAtualizacao: string | null;
}

/** Os 5 campos da seção; a data da última atualização acompanha o título (AC2). */
export const EMBARCACAO_SISTEMAS_DP_FIELDS = [
  "dp",
  "fabricanteModelo",
  "sistemaReferenciaRelativo1",
  "sistemaReferenciaRelativo2",
  "atualizacaoDp",
] as const satisfies readonly (keyof EmbarcacaoSistemasDp)[];

export type EmbarcacaoSistemasDpField = (typeof EMBARCACAO_SISTEMAS_DP_FIELDS)[number];

/**
 * A embarcação não tem nenhuma informação de DP a apresentar — nem os campos,
 * nem a data de atualização. É o estado vazio do padrão global do Hub (AC3),
 * distinto da embarcação inexistente (404).
 */
export function isEmbarcacaoSistemasDpEmpty(sistemasDp: EmbarcacaoSistemasDp): boolean {
  return Object.values(sistemasDp).every((value) => value === null);
}

export type GetEmbarcacaoSistemasDpParams = GetEmbarcacaoSectionParams;

export interface EmbarcacaoSistemasDpState {
  status: EmbarcacaoDetailStatus;
  /** Preenchido apenas em `success`: nenhum outro estado exibe campo da seção (AC4). */
  sistemasDp: EmbarcacaoSistemasDp | null;
  errorMessage: string | null;
}

/**
 * Um tipo de acomodação da embarcação (US-1797 — RN002): uma linha da tabela da
 * seção. As quantidades trafegam como número e são exibidas como armazenadas,
 * sem arredondar nem calcular (AC3); `null` é o "campo sem preenchimento" da
 * célula (AC4). As datas por tipo do contrato (`atualizacaoTipoAcomodacao` e o
 * `ultimaAtualizacao` do tipo) não são colunas do protótipo e ficam de fora.
 */
export interface EmbarcacaoAcomodacaoTipo {
  tipo: string | null;
  quantidade: number | null;
  quantoComporta: number | null;
  comentario: string | null;
}

/**
 * Seção Acomodações do detalhamento da embarcação (US-1797).
 *
 * Reúne as informações gerais de acomodação — enfermaria, academia e a data de
 * atualização (AC2) — e a lista de tipos de acomodação, uma linha por tipo
 * (AC3). Como nas demais seções, `null` é o "campo sem preenchimento" da tela
 * (AC4) e a data da última atualização acompanha o título no cabeçalho.
 */
export interface EmbarcacaoAcomodacoes {
  possuiEnfermaria: string | null;
  possuiAcademia: string | null;
  /** Data de atualização das informações de acomodação, exibida como campo. */
  atualizacaoAcomodacao: string | null;
  /** Data da última atualização da seção, exibida no cabeçalho (AC2). */
  ultimaAtualizacao: string | null;
  acomodacoes: EmbarcacaoAcomodacaoTipo[];
}

/** Os 3 campos gerais da seção, na ordem do AC2; a data da seção fica no título. */
export const EMBARCACAO_ACOMODACOES_FIELDS = [
  "possuiEnfermaria",
  "possuiAcademia",
  "atualizacaoAcomodacao",
] as const satisfies readonly (keyof EmbarcacaoAcomodacoes)[];

export type EmbarcacaoAcomodacoesField = (typeof EMBARCACAO_ACOMODACOES_FIELDS)[number];

/**
 * A embarcação não tem nenhuma informação de acomodação a apresentar — nem os
 * campos gerais, nem a data, nem tipo algum na tabela. É o estado vazio do
 * padrão global do Hub (AC4), distinto da embarcação inexistente (404).
 */
export function isEmbarcacaoAcomodacoesEmpty(acomodacoes: EmbarcacaoAcomodacoes): boolean {
  return (
    acomodacoes.possuiEnfermaria === null &&
    acomodacoes.possuiAcademia === null &&
    acomodacoes.atualizacaoAcomodacao === null &&
    acomodacoes.ultimaAtualizacao === null &&
    acomodacoes.acomodacoes.length === 0
  );
}

export type GetEmbarcacaoAcomodacoesParams = GetEmbarcacaoSectionParams;

export interface EmbarcacaoAcomodacoesState {
  status: EmbarcacaoDetailStatus;
  /** Preenchido apenas em `success`: nenhum outro estado exibe campo da seção (AC4). */
  acomodacoes: EmbarcacaoAcomodacoes | null;
  errorMessage: string | null;
}

/**
 * Um motor da embarcação (US-1800 — RN001): cada registro vira um bloco próprio
 * da seção Motores. Os campos de texto e o identificador do motor chegam como
 * `string | null`; potência, RPM, TBO, periodicidade e a ordem do motor chegam
 * como número (exibido como armazenado, sem arredondar); as duas datas chegam
 * sem hora. Como nas demais seções, `null` é o "campo sem preenchimento" da tela
 * (AC3) e nenhum valor é calculado ou derivado (RN003).
 */
export interface EmbarcacaoMotor {
  /** Identificação do motor (ex.: `MCP 1`), usada como título do bloco (AC2). */
  motor: string | null;
  marca: string | null;
  modelo: string | null;
  numeroSerie: string | null;
  codigoTmMaster: string | null;
  empresaAutomacaoResponsavel: string | null;
  potencia: number | null;
  rpm: number | null;
  tbo: number | null;
  periodicidade: number | null;
  ativo: string | null;
  /** Ordem do motor: critério de ordenação dos blocos, do menor para o maior (RN002). */
  ordemMotor: number | null;
  atualizacaoMotores: string | null;
  /** Data da última atualização das informações do motor. */
  ultimaAtualizacao: string | null;
}

/**
 * Os campos exibidos em cada bloco de motor, na ordem e nas colunas do protótipo
 * (AC3). O `motor` fica de fora: ele encabeça o bloco em vez de virar par rótulo
 * + valor. A data da última atualização acompanha o título da seção.
 */
export const EMBARCACAO_MOTORES_FIELDS = [
  "marca",
  "modelo",
  "numeroSerie",
  "codigoTmMaster",
  "empresaAutomacaoResponsavel",
  "potencia",
  "rpm",
  "tbo",
  "periodicidade",
  "ativo",
  "ordemMotor",
  "atualizacaoMotores",
] as const satisfies readonly (keyof EmbarcacaoMotor)[];

export type EmbarcacaoMotoresField = (typeof EMBARCACAO_MOTORES_FIELDS)[number];

/**
 * A embarcação não tem nenhum motor a apresentar: estado vazio do padrão global
 * do Hub (AC4), distinto da embarcação inexistente (404).
 */
export function isEmbarcacaoMotoresEmpty(motores: EmbarcacaoMotor[]): boolean {
  return motores.length === 0;
}

export type GetEmbarcacaoMotoresParams = GetEmbarcacaoSectionParams;

export interface EmbarcacaoMotoresState {
  status: EmbarcacaoDetailStatus;
  /** Preenchido apenas em `success`: nenhum outro estado exibe motor da seção (AC4). */
  motores: EmbarcacaoMotor[] | null;
  errorMessage: string | null;
}

/** Estado das opções de um filtro de coluna, consumido pelo componente de filtro. */
export interface EmbarcacaoFilterOptionsState {
  options: EmbarcacaoFilterOption[];
  isLoading: boolean;
  hasError: boolean;
}
